import React from "react";
import { View, Text, Image, Platform } from "react-native";

// --- helpers originally in AITutorApp.js ---

const BREAK = "__SEG_BREAK__";

function isWhitespaceOnly(s) {
  return (s ?? "").trim().length === 0;
}

function ratioOf(img) {
  // try aspect_ratio first if provided
  if (Number.isFinite(img?.aspect_ratio)) {
    return img.aspect_ratio;
  }
  // otherwise fall back to width/height
  if (
    Number.isFinite(img?.width) &&
    Number.isFinite(img?.height) &&
    img.height > 0
  ) {
    return img.width / img.height;
  }
  return 1;
}

// decide if an image should sit inline with text or break out as its own block
function isInlineImage(img, prev, next) {
  // We consider it “inline” only if it's visually small and surrounded by text.
  const surroundedByText =
    (prev?.type === "Text" && !isWhitespaceOnly(prev.text)) ||
    (next?.type === "Text" && !isWhitespaceOnly(next.text));

  const isVertical = img.orientation === "vertical";
  const veryWide = (img.aspect_ratio ?? ratioOf(img)) >= 3.5;
  const tallPx = (img.height ?? 0) >= 80;

  // If it's vertical, super wide, or tall, we treat it as a block image (not inline).
  if (isVertical || veryWide || tallPx) return false;

  return surroundedByText;
}

// normalizeSegments:
// - turns <br> in text segments into explicit BREAK markers
// - ensures images have a usable `src`
function normalizeSegments(segments = []) {
  const out = [];
  for (const seg of segments) {
    if (!seg) continue;

    if (seg.type === "Text") {
      const raw = String(seg.text ?? "");

      // Split on <br> tags (loose/forgiving regex) and inject BREAK tokens
      const parts = raw.split(/<br\s*\/?>/i);
      parts.forEach((part, idx) => {
        out.push({ type: "Text", text: part });
        if (idx < parts.length - 1) {
          out.push({ type: BREAK });
        }
      });
    } else if (seg.type === "Image") {
      // images might come as { type:"Image", base64:..., uri:..., src:..., orientation,... }
      const src = seg.src ?? seg.base64 ?? seg.uri;
      if (src) {
        out.push({ ...seg, src });
      }
    }
  }
  return out;
}

// splitIntoParagraphs groups tokens until BREAK markers
function splitIntoParagraphs(tokens) {
  const paras = [];
  let cur = [];

  const flush = () => {
    if (cur.length) paras.push(cur);
    cur = [];
  };

  for (const t of tokens) {
    if (t?.type === BREAK) {
      flush();
    } else {
      cur.push(t);
    }
  }
  flush();
  return paras;
}

// BlockImage = how we render a non-inline image block
function BlockImage({ img }) {
  const hasDims =
    Number.isFinite(img?.width) && Number.isFinite(img?.height);

  const isHoriz = img?.orientation === "horizontal";

  // match your original logic
  const widthStyle =
    hasDims && img.width < 150
      ? img.width
      : isHoriz
      ? "100%"
      : "50%";

  const heightStyle =
    hasDims && img.height < 150
      ? img.height
      : img?.orientation === "vertical"
      ? 300
      : 250;

  return (
    <Image
      source={{ uri: img.src }}
      style={{
        width: widthStyle,
        height: heightStyle,
        resizeMode: "contain",
        alignSelf: "center",
        marginVertical: 5,
      }}
    />
  );
}

// --- main component: SegmentsRendererRN ---
//
// Props:
//   segments: [{type:"Text"|"Image", ...}, ...]
//   isDarkMode: boolean
//   baseFontSize: number
//
// This reproduces how AITutorApp.js was rendering question.segments,
// option.segments, hint.segments, etc.
//
export default function SegmentsRendererRN({
  segments,
  isDarkMode = false,
  baseFontSize = 16,
}) {
  const tokens = normalizeSegments(segments);
  const paras = splitIntoParagraphs(tokens);

  const textStyle = {
    fontSize: baseFontSize,
    lineHeight: Math.round(baseFontSize * 1.5),
    color: isDarkMode ? "#FFF" : "#111827",
    includeFontPadding: false,
  };

  return (
    <View>
      {paras.map((para, i) => {
        // blank line paragraph
        if (!para.length) {
          return (
            <View
              key={i}
              style={{ height: textStyle.lineHeight / 2 }}
            />
          );
        }

        // paragraph that's ONLY images → render them stacked as "block" images
        if (para.every((t) => t.type === "Image")) {
          return (
            <View key={i} style={{ marginBottom: 12 }}>
              {para.map((img, j) => (
                <BlockImage key={j} img={img} />
              ))}
            </View>
          );
        }

        // otherwise: mixed text + small inline imgs → we build rows
        let inlines = [];
        let buffer = "";
        const rows = [];

        const flushBuffer = () => {
          if (buffer.length) {
            inlines.push(buffer);
            buffer = "";
          }
        };

        const pushLine = (key) => {
          // skip if effectively empty
          if (
            !inlines.length ||
            (inlines.length === 1 &&
              typeof inlines[0] === "string" &&
              inlines[0].trim() === "")
          ) {
            inlines = [];
            return;
          }
          const children = inlines.slice(); // copy for safety
          rows.push(
            <Text key={key} style={textStyle}>
              {children}
            </Text>
          );
          inlines = [];
        };

        para.forEach((t, j) => {
          if (t.type === "Text") {
            buffer += t.text ?? "";
            return;
          }

          // If it's an image:
          const prev = para[j - 1];
          const next = para[j + 1];
          const inline = isInlineImage(t, prev, next);

          if (inline) {
            // inline → flush text so far, then add inline <Image /> into current 'line'
            flushBuffer();
            // Inline images here are almost always pre-rendered math (a single
            // symbol or short expression). At baseFontSize * 1.1 they rendered
            // noticeably smaller than the surrounding text and — because inline
            // images align to the text baseline — sat raised like superscripts.
            // Render them a little taller than the text and center them on the
            // line so equations read at full size, in line with the text.
            const height = Math.round(baseFontSize * 1.6);
            const width = Math.round(height * ratioOf(t));
            inlines.push(
              <Image
                key={`inline-${i}-${j}`}
                source={{ uri: t.src }}
                style={{
                  width,
                  height,
                  resizeMode: "contain",
                  marginHorizontal: 3,
                  // Web honors `verticalAlign: middle` for true line-centering;
                  // native falls back to a translateY nudge toward the text
                  // mid-line (baseline center sits ~0.3em above the baseline).
                  ...(Platform.OS === "web"
                    ? { verticalAlign: "middle" }
                    : {
                        transform: [
                          {
                            translateY: Math.round(
                              height / 2 - baseFontSize * 0.8
                            ),
                          },
                        ],
                      }),
                }}
              />
            );
          } else {
            // block-level image in the middle of text flow:
            // close out the current text line(s), then render BlockImage
            flushBuffer();
            pushLine(`ptext-${i}-${j}`);
            rows.push(
              <BlockImage key={`block-${i}-${j}`} img={t} />
            );
          }
        });

        // finish last pending chunk of text for the paragraph
        flushBuffer();
        pushLine(`ptext-end-${i}`);

        return (
          <View key={i} style={{ marginBottom: 12 }}>
            {rows}
          </View>
        );
      })}
    </View>
  );
}
